import { Search, SentimentDissatisfied } from "@mui/icons-material";
import {
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
} from "@mui/material";
import { Box } from "@mui/system";
import axios from "axios";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { config } from "../App";
import Footer from "./Footer";
import Header from "./Header";
import "./Products.css";
import ProductCard from "./ProductCard";
import Cart from "./Cart";
import { generateCartItemsFrom } from "./Cart";

const Products = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [productData, setProduct] = useState([]);
  const [isFetching, setFetched] = useState(false);
  const [productNotFound, setProductNotFound] = useState(false);
  const [timerId, setTimerId] = useState("");
  const [userLoggedIn, setUserLoggedIn] = useState(false);
  const [cartData, setCartData] = useState([]);
  const [userCartItems, setUserCartItems] = useState([]);
  const [userToken, setUserToken] = useState("");

  // Fetch products data and store it
  // Make an API call to get the products list and store it to display the products.

  const performAPICall = async () => {
    try {
      setFetched(true);

      let url = config.endpoint;
      let product = await axios.get(`${url}/products`);
      setProduct(product.data);
      setFetched(false);
      return product.data;
    } catch (err) {
      console.log(err.message);
    }
  };

  //  Implement Search Logic

  const performSearch = async (text) => {
    try {
      // setProductNotFound(false)

      let url = config.endpoint;
      let product = await axios
        .get(`${url}/products/search?value=${text}`)
        .catch((err) => {
          setProductNotFound(true);
        });

      if (product.data) {
        setProductNotFound(false);
        setProduct(product.data);
      }
    } catch (err) {
      console.log(err.message);
    }
  };

  // Optimise API calls with the debounce search implementation
  /**
   * Definition for debounce handler
   * With debounce, this is the function to be called whenever the user type text in the searchbar field
   *
   * @param {{ target: { value: string } }} event
   *    JS event object emitted from the search input field
   *
   * @param {NodeJS.Timeout} debounceTimeout
   *    Timer id set for the previous debounce call
   *
   */

  const debounceSearch = (event, debounceTimeout) => {
    clearTimeout(debounceTimeout);
    // wait for 500ms and make a call
    // first request
    let timerId = setTimeout(() => performSearch(event), 500);
    setTimerId(timerId);
  };

  useEffect(() => {
    async function onLoad() {
      const product = await performAPICall();
      let user = localStorage.getItem("username");
      user && setUserLoggedIn(true);
      let token = localStorage.getItem("token");
      if (token) {
        setUserToken(token);
        const cartItems = await fetchCart(token);
        setUserCartItems(cartItems); // Array of objects with productId and quantity of products in cart
        const cartData = await generateCartItemsFrom(cartItems, product);
        setCartData(cartData);
      }
    }
    onLoad();
  }, []);

  const fetchCart = async (token) => {
    if (!token) return;

    try {
      let url = config.endpoint + "/cart";
      let cartData = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });
      return cartData.data;
    } catch (err) {
      if (err.response && err.response.status === 400) {
        enqueueSnackbar(err.response.data.message, { variant: "error" });
      } else {
        enqueueSnackbar(
          "Could not fetch cart details. Check that the backend is running, reachable and returns valid JSON.",
          { variant: "error" }
        );
      }
      return null;
    }
  };

  const isItemInCart = (items, productId) => {
    // Items is entire array data
    for (let i = 0; i < items.length; i++) {
      // console.log(items[i])
      if (items[i]["_id"] === productId) {
        enqueueSnackbar(
          "Item already in cart. Use the cart sidebar to update quantity or remove item.",
          { variant: "warning" }
        );
        return true;
      }
    }
    return false;
  };

  const addToCart = async (
    token,
    items,
    products,
    productId,
    qty,
    options = { preventDuplicate: false }
  ) => {
    if (options.preventDuplicate === true) {
      try {
        let url = config.endpoint + "/cart";
        let res = await axios.post(
          url,
          { productId: productId, qty: qty },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const cartData = await generateCartItemsFrom(res.data, products);
        setCartData(cartData);
      } catch (err) {
        console.log(err);
      }
    } else {
      // Update only Qunatity
      // items.quantity++
      let index;
      for (let i = 0; i < items.length; i++) {
        if (items[i]["productId"] === productId) {
          index = i;
        }
      }
      if (options.preventDuplicate === "handleAdd") {
        items[index]["qty"]++;
      } else {
        items[index]["qty"]--;
      }
      // Update Items
      let url = config.endpoint + "/cart";
      let res = await axios.post(
        url,
        { productId: productId, qty: items[index]["qty"] },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const cartData = await generateCartItemsFrom(res.data, products);
      setCartData(cartData);
    }
  };

  let addItems = (e) => {
    if (!userLoggedIn) {
      enqueueSnackbar("Login to add an item to the Cart", {
        variant: "warning",
      });
    } else {
      let result = isItemInCart(cartData, e.target.value);
      if (!result) {
        addToCart(userToken, userCartItems, productData, e.target.value, 1, {
          preventDuplicate: true,
        });
      } else {
        enqueueSnackbar(
          "Item already in the Cart. Use the cart sidebar to update quantity or remove item.",
          { variant: "warning" }
        );
      }
    }
  };

  const onButtonClick = (id, handle) => {
    console.log("Button Click");
    console.log(id, handle);
    // Token, items, products, productId, Qunatity, Options = { preventDuplicate: false }
    addToCart(userToken, userCartItems, productData, id, null, {
      preventDuplicate: handle,
    });
  };

  return (
    <div>
      <Header hasHiddenAuthButtons={false}>
        {/* Display search bar in the header for products page */}
        <TextField
          className="search-desktop"
          size="small"
          onChange={(e) => {
            debounceSearch(e.target.value, timerId);
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Search color="primary" />
              </InputAdornment>
            ),
          }}
          placeholder="Search for items/categories"
          name="search"
        />
      </Header>

      {/* Search view for mobiles */}
      <TextField
        className="search-mobile"
        size="small"
        fullWidth
        onChange={(e) => {
          debounceSearch(e.target.value, timerId);
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Search color="primary" />
            </InputAdornment>
          ),
        }}
        placeholder="Search for items/categories"
        name="search"
      />
      <Grid container justifyContent="center">
        <Grid item className="product-grid">
          <Box className="hero">
            <p className="hero-heading">
              India’s <span className="hero-highlight">FASTEST DELIVERY</span>{" "}
              to your door step
            </p>
          </Box>
        </Grid>
      </Grid>

      {isFetching ? <div className={"loading"}>
                      <CircularProgress />
                      <h3>Loading Products</h3>
                    </div>
                  : !productNotFound?
                  <>
                  {
                    !userLoggedIn?
                    <Grid container >
                      <Grid container spacing={{ xs: 2, md: 3, lg: 1}} >
                      {productData.map((x)=>
                         (<Grid item lg={3} xs={6} md={6} sm={6} mt={2} mb={2} key={x['_id']}  >
                          <ProductCard product={x} handleAddToCart={(e)=>{addItems(e)}}/>
                        </Grid>
                        )
                        )}
                    </Grid>
                      </Grid>:
                        <Grid container  >
                           <Grid container spacing={{ xs: 2, md: 3, lg: 1}} md={9} >
                          {productData.map((x)=>
                             (<Grid item lg={3} xs={6} md={6} sm={6} mt={2} mb={2} key={x['_id']}  >
                              <ProductCard product={x} handleAddToCart={(e)=>{addItems(e)}}/>
                            </Grid>
                            )
                            )}
                    </Grid>
                        <Grid item md={3} sm={12} xs={12} sx={{backgroundColor:'#E9F5E1'}} >
                           <Cart product={productData} items={cartData} handleQuantity={onButtonClick}  />
                           {/* handleQuantity={onButtonClick} */}
                        </Grid>
                      </Grid>
                  }
                  </>
                    :<div className={"loading"}>
                    <SentimentDissatisfied/>
                    <h3>No products found</h3>
                  </div>          
      }
      <Footer />
    </div>
  );
};

export default Products;
       
